import { JSX } from "react";
import toast, { ToastOptions, Toast } from "react-hot-toast";
import { CheckCircle2, XCircle, AlertTriangle, Info, Loader2, X } from "lucide-react";

const TOAST_STYLES = {
  container:
    "pointer-events-auto flex w-full max-w-sm rounded-xl bg-white/95 p-3 backdrop-blur-md transition-all duration-300",
  content: "flex flex-1 items-start gap-3",
  iconWrapper: "shrink-0",
  textWrapper: "flex-1 space-y-1",
  title: "font-semibold text-sm text-gray-900",
  message: "text-sm text-gray-600 leading-relaxed",
  closeButton: "shrink-0 rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
};

// Enhanced animations with bounce effect
const ANIMATION = "animate-in slide-in-from-top-2 fade-in zoom-in-95 duration-300 ease-out";
const EXIT_ANIMATION = "animate-out slide-out-to-top-2 fade-out zoom-out-95 duration-200";

// Theme-aware variant styles with colored borders, gradients, and shadows
const VARIANT_STYLES = {
  default: {
    border: "border-l-4 border-gray-400",
    bg: "bg-gradient-to-r from-gray-50/95 to-white/95",
    iconBg: "bg-gray-100",
    iconColor: "text-gray-600"
  },
  success: {
    border: "border-l-4 border-success",
    bg: "bg-gradient-to-r from-success/5 to-white/95",
    iconBg: "bg-success/10",
    iconColor: "text-success"
  },
  error: {
    border: "border-l-4 border-error",
    bg: "bg-gradient-to-r from-error/5 to-white/95",
    iconBg: "bg-error/10",
    iconColor: "text-error"
  },
  warning: {
    border: "border-l-4 border-warning",
    bg: "bg-gradient-to-r from-warning/5 to-white/95",
    iconBg: "bg-warning/10",
    iconColor: "text-warning-dark"
  },
  info: {
    border: "border-l-4 border-primary",
    bg: "bg-gradient-to-r from-primary/5 to-white/95",
    iconBg: "bg-primary/10",
    iconColor: "text-primary"
  }
};

// Toast Duration Constants (in ms)
const TOAST_DURATION = {
  DEFAULT: 4000,
  SUCCESS: 4000,
  ERROR: 5000,
  WARNING: 4000,
  INFO: 4000,
  LOADING: Infinity,
  RECORDING: 3000,
  RECORDING_ERROR: 5000
} as const;

/** Reusable Toast Component using Glassmorphism design with theme colors */
const ToastItem = ({
  t,
  title,
  message,
  icon,
  variant = "default"
}: {
  t: Toast;
  title?: string;
  message: string;
  icon: JSX.Element;
  variant?: "default" | "success" | "error" | "warning" | "info";
}) => {
  const variantStyles = VARIANT_STYLES[variant];

  return (
    <div
      className={`${t.visible ? ANIMATION : EXIT_ANIMATION} ${TOAST_STYLES.container} ${variantStyles.border} ${variantStyles.bg}`}
    >
      <div className={TOAST_STYLES.content}>
        <div className={`${TOAST_STYLES.iconWrapper} ${variantStyles.iconBg} p-1.5 rounded-lg`}>{icon}</div>
        <div className={TOAST_STYLES.textWrapper}>
          {title && <p className={TOAST_STYLES.title}>{title}</p>}
          <p className={TOAST_STYLES.message}>{message}</p>
        </div>
        <button
          onClick={() => toast.dismiss(t.id)}
          className={TOAST_STYLES.closeButton}
          aria-label="Close notification"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

/** Show a success toast notification */
export const showSuccess = (message: string, options?: Partial<ToastOptions>): string => {
  return toast.custom(
    (t) => (
      <ToastItem
        t={t}
        title="Success"
        message={message}
        variant="success"
        icon={<CheckCircle2 className="h-5 w-5 text-success" />}
      />
    ),
    { duration: TOAST_DURATION.SUCCESS, ...options }
  );
};

/** Show an error toast notification */
export const showError = (message: string, options?: Partial<ToastOptions>): string => {
  return toast.custom(
    (t) => (
      <ToastItem
        t={t}
        title="Error"
        message={message}
        variant="error"
        icon={<XCircle className="h-5 w-5 text-error" />}
      />
    ),
    { duration: TOAST_DURATION.ERROR, ...options }
  );
};

/** Show a warning toast notification */
export const showWarning = (message: string, options?: Partial<ToastOptions>): string => {
  return toast.custom(
    (t) => (
      <ToastItem
        t={t}
        title="Warning"
        message={message}
        variant="warning"
        icon={<AlertTriangle className="h-5 w-5 text-warning-dark" />}
      />
    ),
    { duration: TOAST_DURATION.WARNING, ...options }
  );
};

/** Show an info toast notification */
export const showInfo = (message: string, options?: Partial<ToastOptions>): string => {
  return toast.custom(
    (t) => (
      <ToastItem t={t} title="Info" message={message} variant="info" icon={<Info className="h-5 w-5 text-primary" />} />
    ),
    { duration: TOAST_DURATION.INFO, ...options }
  );
};

/** Show a loading toast notification */
export const showLoading = (message: string, options?: Partial<ToastOptions>): string => {
  return toast.custom(
    (t) => (
      <ToastItem
        t={t}
        title="Loading..."
        message={message}
        variant="info"
        icon={<Loader2 className="h-5 w-5 animate-spin text-primary" />}
      />
    ),
    { duration: TOAST_DURATION.LOADING, ...options }
  );
};

/** Show a custom toast with JSX content */
export const showCustom = (
  content: (t: { id: string; visible: boolean }) => JSX.Element,
  options?: Partial<ToastOptions>
): string => {
  return toast.custom(content, options);
};

/** Show a promise-based toast */
export const showPromise = <T,>(
  promise: Promise<T>,
  messages: {
    loading: string;
    success: string | ((data: T) => string);
    error: string | ((err: Error) => string);
  },
  options?: Partial<ToastOptions>
): Promise<T> => {
  // For a truly consistent look, we'll manually manage the lifecycle
  const toastId = showLoading(messages.loading, options);

  promise
    .then((data) => {
      const successMessage = typeof messages.success === "function" ? messages.success(data) : messages.success;

      toast.custom(
        (t) => (
          <ToastItem
            t={t}
            title="Success"
            message={successMessage}
            variant="success"
            icon={<CheckCircle2 className="h-5 w-5 text-success" />}
          />
        ),
        { id: toastId, duration: TOAST_DURATION.SUCCESS }
      );
      return data;
    })
    .catch((err) => {
      const errorMessage = typeof messages.error === "function" ? messages.error(err) : messages.error;

      toast.custom(
        (t) => (
          <ToastItem
            t={t}
            title="Error"
            message={errorMessage}
            variant="error"
            icon={<XCircle className="h-5 w-5 text-error" />}
          />
        ),
        { id: toastId, duration: TOAST_DURATION.ERROR }
      );
      throw err;
    });

  return promise;
};

/** Dismiss a specific toast or all toasts */
export const dismissToast = (toastId?: string): void => {
  if (toastId) {
    toast.dismiss(toastId);
  } else {
    toast.dismiss();
  }
};

/** Notification helper: Recording Started */
export const showRecordingStarted = (title: string, message: string): string => {
  return toast.custom(
    (t) => (
      <ToastItem
        t={t}
        title={title}
        message={message}
        variant="error"
        icon={
          <span className="relative flex h-3 w-3 mt-1">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-error opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-error"></span>
          </span>
        }
      />
    ),
    { duration: TOAST_DURATION.RECORDING_ERROR }
  );
};

/** Notification helper: Recording Stopped */
export const showRecordingStopped = (title: string, message: string): string => {
  return toast.custom(
    (t) => (
      <ToastItem
        t={t}
        title={title}
        message={message}
        variant="success"
        icon={<div className="w-3 h-3 mt-1 rounded-full bg-slate-400"></div>}
      />
    ),
    { duration: TOAST_DURATION.RECORDING }
  );
};

// Export default toast object for compatibility
export { toast };

// Grouped export for easy importing
export const toastNotify = {
  success: showSuccess,
  error: showError,
  warning: showWarning,
  info: showInfo,
  loading: showLoading,
  custom: showCustom,
  promise: showPromise,
  dismiss: dismissToast,
  recordingStarted: showRecordingStarted,
  recordingStopped: showRecordingStopped
};

export default toastNotify;
